import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Sparkles, Cpu, LineChart, Phone } from "lucide-react";

/**
 * Mobile-only fixed bottom tab bar — gives one-tap navigation to the five
 * highest-intent destinations. Hidden from md+ screens.
 *
 * Tabs route differently depending on where the user is:
 *   - On `/` (landing), most tabs scroll to in-page anchors.
 *   - The "Services" tab always routes to `/services` (the dedicated long-form
 *     services page) regardless of which page the user is on.
 *   - On `/services`, Home / AI / Results / Contact route back to "/" + the
 *     matching anchor.
 */
const TABS = [
  { id: "top", label: "Home", icon: Home, kind: "anchor" },
  { id: "services", label: "Services", icon: Sparkles, kind: "route", path: "/services" },
  { id: "ai-solutions", label: "AI", icon: Cpu, kind: "anchor" },
  { id: "case-studies", label: "Results", icon: LineChart, kind: "anchor" },
  { id: "contact", label: "Contact", icon: Phone, kind: "anchor" },
];

export default function MobileBottomNav() {
  const [active, setActive] = useState("top");
  const location = useLocation();
  const navigate = useNavigate();
  const onServicesPage = location.pathname.startsWith("/services");

  useEffect(() => {
    // On the services page, the Services tab is always active.
    if (onServicesPage) {
      setActive("services");
      return undefined;
    }
    // On landing, observe the four anchor sections.
    const anchorTabs = TABS.filter((t) => t.kind === "anchor");
    const targets = anchorTabs
      .map((t) => document.getElementById(t.id))
      .filter(Boolean);
    if (targets.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        rootMargin: "-30% 0px -50% 0px",
        threshold: [0, 0.1, 0.25, 0.5],
      }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [onServicesPage]);

  const handleClick = (e, tab) => {
    e.preventDefault();
    if (tab.kind === "route") {
      navigate(tab.path);
      setActive(tab.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Anchor tab — if we're on a non-landing route, route home first
    if (location.pathname !== "/") {
      navigate(`/#${tab.id}`);
      setActive(tab.id);
      return;
    }
    const el = document.getElementById(tab.id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActive(tab.id);
    }
  };

  return (
    <motion.nav
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.6, duration: 0.45, ease: "easeOut" }}
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-[55] pb-[env(safe-area-inset-bottom)]"
      aria-label="Mobile navigation"
    >
      <div className="absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />

      <div className="mx-3 mb-3 rounded-2xl glass-strong border border-white/[0.07] shadow-[0_-10px_30px_-12px_rgba(0,0,0,0.65)]">
        <ul className="grid grid-cols-5 h-16">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = active === tab.id;
            return (
              <li key={tab.id} className="flex">
                <a
                  href={tab.kind === "route" ? tab.path : `#${tab.id}`}
                  onClick={(e) => handleClick(e, tab)}
                  data-testid={`mobile-tab-${tab.id}`}
                  aria-current={isActive ? "page" : undefined}
                  className="relative flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-mono-acc tracking-wider uppercase transition-colors"
                >
                  {isActive && (
                    <motion.span
                      layoutId="mobile-tab-indicator"
                      className="absolute top-1.5 h-[2px] w-7 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <Icon
                    size={18}
                    strokeWidth={isActive ? 2.2 : 1.6}
                    className={isActive ? "text-white" : "text-zinc-400"}
                  />
                  <span className={isActive ? "text-white" : "text-zinc-500"}>
                    {tab.label}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.nav>
  );
}
